import { forwardRef, useEffect, useMemo } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import { useDispatch, useSelector } from "react-redux";
import { closePosLocationsDialog } from "../../../../app/slices/posSlice";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const DEFAULT_CENTER = [33.325891, 44.47519];

// Pin-shaped marker so the device locations stand out from the default icon
// used by the all-POS map.
const markerIcon = L.divIcon({
  html: `
    <div style="position: relative; width: 30px; height: 40px;">
      <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M15 0C9.477 0 5 4.477 5 10c0 7.5 10 20 10 20s10-12.5 10-20c0-5.523-4.477-10-10-10z"
          fill="#7b1fa2"
          stroke="#fff"
          stroke-width="2"
        />
        <circle cx="15" cy="10" r="4" fill="#fff" />
      </svg>
    </div>
  `,
  className: "",
  iconSize: [30, 40],
  iconAnchor: [15, 40],
  popupAnchor: [0, -40],
});

// Keeps every pin in view: a single location is centered, several are framed.
const FitBounds = ({ positions }) => {
  const map = useMap();

  useEffect(() => {
    if (positions.length === 1) {
      map.setView(positions[0], 16);
    } else if (positions.length > 1) {
      map.fitBounds(positions, { padding: [50, 50] });
    }
  }, [map, positions]);

  return null;
};

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PosLocationsMap = () => {
  const dispatch = useDispatch();

  const items = useSelector((state) => state.pointOfsales.posLocations);

  const locations = useMemo(
    () =>
      (items?.data || []).filter(
        (location) =>
          location.latitude !== null &&
          location.longitude !== null &&
          !isNaN(Number(location.latitude)) &&
          !isNaN(Number(location.longitude)),
      ),
    [items?.data],
  );

  const positions = useMemo(
    () =>
      locations.map((location) => [
        Number(location.latitude),
        Number(location.longitude),
      ]),
    [locations],
  );

  const handleDialog = () => {
    dispatch(closePosLocationsDialog());
  };

  return (
    <Dialog
      open={items.dialog}
      TransitionComponent={Transition}
      keepMounted
      fullWidth={true}
      maxWidth="lg"
      dir="rtl"
      onClose={() => {
        handleDialog();
      }}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogContent>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h4" color={"purple"} component="div">
            مواقع نقطة البيع
            {items?.pos?.name ? ` - ${items.pos.name}` : ""}
          </Typography>
          {!items.loading && (
            <Chip
              label={`عدد المواقع: ${locations.length}`}
              color="secondary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            {items.loading ? (
              <Box
                sx={{
                  height: 800,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ width: "50%" }}>
                  <LinearProgress />
                </Box>
              </Box>
            ) : locations.length === 0 ? (
              <Box
                sx={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  لا توجد مواقع مسجلة لنقطة البيع
                </Typography>
              </Box>
            ) : (
              <MapContainer
                center={positions[0] ?? DEFAULT_CENTER}
                zoom={16}
                scrollWheelZoom={true}
                style={{ height: 800, width: "100%" }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds positions={positions} />
                {locations.map((location, index) => (
                  <Marker
                    key={location.id ?? index}
                    position={positions[index]}
                    icon={markerIcon}
                  >
                    <Popup>
                      <Box sx={{ minWidth: 200 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700 }}
                        >
                          {items?.pos?.name ?? "نقطة البيع"}
                        </Typography>
                        <Typography variant="body2">
                          الرقم التسلسلي: {location.serial_number ?? "-"}
                        </Typography>
                        <Typography variant="body2">
                          معرف الجهاز: {location.device_id ?? "-"}
                        </Typography>
                        <Typography variant="body2">
                          الإحداثيات: {location.latitude}, {location.longitude}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="a"
                          href={`https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          فتح في خرائط جوجل
                        </Typography>
                      </Box>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default PosLocationsMap;
